import { FC, useState } from 'react';
import { Formik } from 'formik';
import { Form } from 'react-router-dom';
import { Box, FormControl, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';

import CustomButton from '../../../../../../../common/CustomButton';
import InformationIcon from '../../../../../../../assets/icons/Info, Infornation.png';
import { useAppDispatch, useAppSelector } from '../../../../../../../hooks/redux';
import { useUpdateUserAdditionalInfoMutation } from '../../../../../../../store/services/customizeOptionsApi';
import { setUserInfo } from '../../../../../../../store/slices/userSlice';
import { EconomyIcon, MidRangeIcon, LuxuryIcon, DoesntMatterIcon } from '../../../../../../../components/Icons';
import { generateActiveGradient } from '../../../../../../../utils/gradientUtils';

import styles from './BudgetPreferences.module.scss';

type FormikValues = {
  budget?: string;
};

type BudgetPreferencesProps = {
  closeModal: () => void;
};

const BudgetPreferences: FC<BudgetPreferencesProps> = ({ closeModal }) => {
  const dispatch = useAppDispatch();
  const user = useAppSelector(state => state.user);
  const [updateUserAdditionalInfo, { isSuccess }] = useUpdateUserAdditionalInfoMutation();

  const [resErrors, setResErrors] = useState<{ attr: string | null; code: string; detail: string }[] | null>(null);

  const initialValues = {
    budget: user.budget_preference,
  };

  const handleSubmit = async (values: FormikValues) => {
    const payload = {
      budget_preference: values.budget,
    };

    try {
      await updateUserAdditionalInfo(payload);
      dispatch(setUserInfo(payload));
      setResErrors(null);
      closeModal();
    } catch (error) {
      console.error('Error updating user info:', error);
      setResErrors((error as any)?.data?.errors || null);
    }
  };

  const formikValidate = (values: FormikValues) => {
    const errors: FormikValues = {};

    return errors;
  };

  return (
    <Box className={styles.BudgetPreferences}>
      <Formik
        initialValues={initialValues}
        validate={formikValidate}
        onSubmit={handleSubmit}
      >
        {({ values, handleChange, handleSubmit, isSubmitting }) => (
          <Form
            className={styles.BudgetPreferencesForm}
            onSubmit={handleSubmit}
          >
            <FormControl
              component="fieldset"
              className={styles.radioGroupWrapper}
            >
              <Typography
                variant="h5Bold"
                className={styles.stepTitle}
              >
                Let's personalize your AI assistant
              </Typography>
              <Typography
                variant="h10"
                className={styles.stepSubtitle}
              >
                Budget Preferences
              </Typography>
              <RadioGroup
                name="budget"
                className={styles.radioGroup}
                value={values.budget}
                onChange={handleChange}
              >
                {[
                  { value: 'Economy', name: 'Economy', IconComponent: EconomyIcon, iconSize: 45 },
                  { value: 'Mid-range', name: 'Mid-Range', IconComponent: MidRangeIcon, iconSize: 45 },
                  { value: 'Luxury', name: 'Luxury', IconComponent: LuxuryIcon, iconSize: 45 },
                  { value: "Doesn't matter", name: "Doesn't Matter", IconComponent: DoesntMatterIcon, iconSize: 45 },
                ].map(val => {
                  const { IconComponent, iconSize } = val;
                  const isSelected = values.budget === val.value;
                  return (
                    <FormControlLabel
                      key={val.value}
                      value={val.value}
                      control={<Radio sx={{ display: 'none' }} />}
                      label={
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '16px',
                            ...(isSelected
                              ? {
                                  background:
                                    'linear-gradient(white, white) padding-box, linear-gradient(231.69deg, #15C3FF 20.67%, #8DF4DB 100%) border-box',
                                  border: '2px solid transparent',
                                }
                              : {
                                  border: '1px solid #BDC9EE',
                                }),
                            borderRadius: '12px',
                            backgroundColor: '#fff',
                            cursor: 'pointer',
                            width: '200px',
                            height: '130px',
                            gap: '8px',
                          }}
                        >
                          <IconComponent
                            size={iconSize}
                            color={isSelected ? undefined : '#BDC9EE'}
                            gradient={isSelected ? generateActiveGradient(val.value, 'budget') : undefined}
                          />
                          <Typography
                            variant="h10"
                            sx={{ textAlign: 'center' }}
                          >
                            {val.name.replace('_', ' ')}
                          </Typography>
                        </Box>
                      }
                    />
                  );
                })}
              </RadioGroup>
            </FormControl>

            <Box className={styles.additionalInfo}>
              <Typography
                variant="h11"
                className={styles.additionalInfoText}
              >
                To get badges, fill in all the questions
              </Typography>
              <img
                src={InformationIcon}
                className={styles.additionalInfoImg}
                alt="info"
              />
            </Box>

            <Box className={styles.cityAndAiroportBtns}>
              <CustomButton
                btnType="button"
                variant="outlined"
                onClick={closeModal}
              >
                Cancel
              </CustomButton>
              <CustomButton
                btnType="submit"
                customClass={styles.customButton}
                disabled={isSubmitting}
              >
                Save
              </CustomButton>
            </Box>
            {isSuccess && (
              <Typography
                variant="h12"
                sx={{ color: '#208B64' }}
              >
                Budget Preferences updated successfully.
              </Typography>
            )}
            {resErrors &&
              resErrors.map((error, index) => (
                <Typography
                  key={index}
                  variant="h12"
                  sx={{ color: '#C8102E' }}
                >
                  {error.detail}
                </Typography>
              ))}
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default BudgetPreferences;
